import React from 'react';
import { isEmpty } from 'lodash';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';

import { TableView } from './TableView';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { MetadataContext } from 'contexts/MetadataContext';
import { SettingsContext } from 'contexts/SettingsContext';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { convertToArray } from 'Details/utils';
import { isCustomCategory, EMPTY_VALUE_NULL } from 'Details/utils';

import {
	Wrapper,
	LoaderContainer,
	TableContainer,
} from './TaxonomyGrid.styles';
import { chartFilterColumns } from 'Explore/tableMetadata';

export class TaxonomyGrid extends React.Component {
	state = {
		isTaxonomyTableReady: false,
	};

	setIsTaxonomyTableReady = isReady => {
		this.setState({ isTaxonomyTableReady: isReady });
	};

	getCustomFilters() {
		const { contextParams } = this.props;
		let {
			storedQueryId,
			customFiltersValues,
			customFilters,
			storedQuery,
		} = contextParams;

		if (storedQueryId) {
			if (storedQuery) {
				({ customFiltersValues, customFilters } = storedQuery);

				customFiltersValues = customFiltersValues.map(
					item => (item === '' || item === null ? EMPTY_VALUE_NULL : item)
				);
			} else {
				customFiltersValues = [];
				customFilters = [];
			}
		}
		return { customFiltersValues, customFilters };
	}

	renderTable() {
		const { isTaxonomyTableReady } = this.state;
		const {
			apTaxonomy,
			companiesList,
			contextParams,
			customFilterOptions,
			clearApiCache,
			apiCacheTrigger,
			permissions,
			categoryFilter,
			status,
		} = this.props;
		const {
			dateFrom,
			dateTo,
			hierarchy,
			measures,
			dimensions,
			showHarmonizedVendors,
			waterfallFilter,
			segmentName,
		} = contextParams;

		const { customFiltersValues, customFilters } = this.getCustomFilters();
		const customFiltersValuesArray = convertToArray(customFiltersValues);
		const hierarchyArray = isEmpty(hierarchy)
			? measures
			: convertToArray(hierarchy);

		const grouppingNamesMap = {};
		const extraFilters = {};
		const chartFilterValues = Object.keys(chartFilterColumns).concat([
			'customColumnKey',
			'customColumnValue',
		]);
		chartFilterValues.forEach(key => {
			const value = contextParams[key];
			if (!!value) {
				extraFilters[key] = isEmpty(value) ? null : value;
			}
		});

		if (waterfallFilter) {
			extraFilters[waterfallFilter[0]] = waterfallFilter[1];
		}
		let clientCategoryFilters = [];
		if (categoryFilter) {
			Object.keys(categoryFilter).forEach(key => {
				clientCategoryFilters.push({
					columnName: key,
					value: categoryFilter[key],
				});
			});
		}
		const filtersData = {
			dateFrom,
			dateTo,
			harmonized: showHarmonizedVendors,
			showOriginalAPTaxonomy: apTaxonomy,
			segmentName: isEmpty(segmentName) ? null : segmentName,
			filters: [
				...convertToArray(customFilters)
					.map((item, index) => {
						const customFilterOriginal = customFilterOptions.find(
							customItem => customItem.name === item
						);

						return {
							columnName: customFilterOriginal
								? customFilterOriginal.columnName
								: '',
							value: customFiltersValuesArray[index],
						};
					})
					.map(({ value, ...rest }) => ({
						...rest,
						value: isEmpty(value) ? '' : value,
					}))
					// group filters on same columnName to have value as array
					.reduce((acc, { columnName, value }) => {
						if (grouppingNamesMap[columnName]) {
							grouppingNamesMap[columnName].value = Array.isArray(
								grouppingNamesMap[columnName].value
							)
								? [...grouppingNamesMap[columnName].value, value]
								: [grouppingNamesMap[columnName].value, value];

							return acc;
						} else {
							const filter = { columnName, value };
							grouppingNamesMap[columnName] = filter;

							return [...acc, filter];
						}
					}, []),
				{ columnName: 'spendType', value: apTaxonomy ? 'INDIRECT' : 'DIRECT' },
				...clientCategoryFilters,
			],
			hierarchy: hierarchyArray,
			...extraFilters,
		};

		return (
			<React.Fragment>
				{!isTaxonomyTableReady ? (
					<LoaderContainer>
						<ApLoaderDefault loaderText={() => <ApLoaderText />} />
					</LoaderContainer>
				) : null}
				<TableContainer isVisible={isTaxonomyTableReady}>
					<TableView
						apTaxonomy={apTaxonomy}
						companiesList={companiesList}
						contextParams={contextParams}
						filtersData={filtersData}
						isCustomCategory={isCustomCategory(dimensions)}
						clearApiCache={clearApiCache}
						apiCacheTrigger={apiCacheTrigger}
						setIsReady={this.setIsTaxonomyTableReady}
						permissions={permissions || []}
						timestamp={
							status.lastPublishedDate || status.lastSuccessClassificationDate
						}
						customFilterOptions={customFilterOptions}
					/>
				</TableContainer>
			</React.Fragment>
		);
	}

	handleNavigationBackButtonClick = () => {
		const { location, history } = this.props;
		const { prevPageUrl } = location.state;

		const locationStateCleanup = {
			prevPageUrl: null,
			selectedDimensionKey: null,
			selectedDimensionName: null,
		};

		history.push(prevPageUrl, locationStateCleanup);
	};

	render() {
		return <Wrapper>{this.renderTable()}</Wrapper>;
	}
}

export default props => (
	<PermissionsContext.Consumer>
		{({ permissions }) => (
			<UploadStateContext.Consumer>
				{({ status }) => (
					<ApiDataContext.Consumer>
						{({ clearApiCache, apiCacheTrigger }) => (
							<MetadataContext.Consumer>
								{({ metadata }) => (
									<SettingsContext.Consumer>
										{({ customFilters, companiesList }) => (
											<QueryStringContext.Consumer>
												{queryStringParams => {
													const contextParams = { ...queryStringParams };
													if (contextParams.selectedDimensionKey) {
														contextParams[contextParams.selectedDimensionKey] =
															contextParams.selectedDimensionName;
													}
													if (props.apTaxonomy) contextParams.company = null;

													return (
														<TaxonomyGrid
															{...props}
															companiesList={companiesList}
															contextParams={contextParams}
															dimensionOptions={metadata.dimensions}
															customFilterOptions={customFilters}
															clearApiCache={clearApiCache}
															apiCacheTrigger={apiCacheTrigger}
															permissions={permissions}
															status={status}
														/>
													);
												}}
											</QueryStringContext.Consumer>
										)}
									</SettingsContext.Consumer>
								)}
							</MetadataContext.Consumer>
						)}
					</ApiDataContext.Consumer>
				)}
			</UploadStateContext.Consumer>
		)}
	</PermissionsContext.Consumer>
);
